import type { CatalogCategoryDTO } from "../../api/types.js";
import { messages } from "../../i18n/messages.js";

interface AllCategoriesViewProps {
  categories: CatalogCategoryDTO[];
  collections: CatalogCategoryDTO[];
  onBack: () => void;
  onSelectCategory: (categoryId: string) => void;
  onSelectCollection: (collectionId: string) => void;
  /** Staging-only category count strip (081) */
  showStagingCounts?: boolean;
}

function CategoryButton({
  label,
  count,
  active,
  onClick,
  showCount,
}: {
  label: string;
  count?: number;
  active?: boolean;
  onClick: () => void;
  showCount?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "flex w-full items-center justify-between rounded-[14px] bg-[var(--tg-theme-button-color,#3390ec)] px-4 py-3 text-left text-sm font-medium text-[var(--tg-theme-button-text-color,#fff)]"
          : "flex w-full items-center justify-between rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-4 py-3 text-left text-sm font-medium"
      }
    >
      <span>{label}</span>
      {showCount && count != null ? <span className="text-xs opacity-70">{count}</span> : null}
    </button>
  );
}

export function AllCategoriesView({
  categories,
  collections,
  onBack,
  onSelectCategory,
  onSelectCollection,
  showStagingCounts = false,
}: AllCategoriesViewProps) {
  const visibleCategories = categories.filter(
    (item) =>
      item.kind !== "collection" &&
      (item.available_product_count ?? item.product_count ?? 0) > 0,
  );

  return (
    <section className="flex flex-1 flex-col gap-4 px-4 py-4 pb-28">
      <button
        type="button"
        onClick={onBack}
        className="min-h-11 text-left text-sm font-medium text-[var(--tg-theme-link-color,#3390ec)]"
      >
        {messages.common.back}
      </button>

      <h2 className="text-lg font-semibold">{messages.catalog.allCategories}</h2>
      {showStagingCounts ? (
        <p className="text-sm opacity-70">
          {visibleCategories.length} {messages.catalog.categoryCountLabel}
          <span className="opacity-50"> · staging</span>
        </p>
      ) : null}

      <div className="grid gap-2">
        {collections.map((item) => (
          <CategoryButton
            key={`collection-${item.id}`}
            label={item.label}
            count={item.available_product_count ?? item.product_count}
            showCount={showStagingCounts}
            onClick={() => onSelectCollection(item.id)}
          />
        ))}
        {visibleCategories.map((item) => (
          <CategoryButton
            key={`category-${item.id}`}
            label={item.label}
            count={item.available_product_count ?? item.product_count}
            showCount={showStagingCounts}
            onClick={() => onSelectCategory(item.id)}
          />
        ))}
      </div>
    </section>
  );
}
